import type { WriteRejection, WriteResult } from "@estelle/domain";

/** What happened to one item inside a bulk operation. */
export type BulkOutcome<T> =
  | { item: T; status: "success" }
  | { item: T; status: "rejected"; rejection: WriteRejection }
  | { item: T; status: "error"; message: string };

/** How many of a batch's outcomes landed in each bucket. */
export interface BulkSummary {
  succeeded: number;
  failed: number;
  total: number;
}

export function summarize<T>(outcomes: BulkOutcome<T>[]): BulkSummary {
  const succeeded = outcomes.filter(
    (outcome) => outcome.status === "success",
  ).length;
  return {
    succeeded,
    failed: outcomes.length - succeeded,
    total: outcomes.length,
  };
}

/** An item a bulk report can retry — the outcome plus the input that produced it. */
export function failedItems<T>(outcomes: BulkOutcome<T>[]): T[] {
  return outcomes
    .filter((outcome) => outcome.status !== "success")
    .map((outcome) => outcome.item);
}

/**
 * At most this many writes in flight at once (#170's ticket text: "run with
 * small concurrency (at most 3) under the throttle"). Each write already
 * retries up to three times on its own if Shopify throttles it
 * (`sync/write-through.ts`'s `MAX_ATTEMPTS`); capping how many a bulk edit
 * fires at once is what keeps thirty near-simultaneous HSN edits from
 * emptying the bucket before any of them gets to retry.
 */
export const BULK_CONCURRENCY = 3;

/**
 * Run one write per item, independently (FR-CAT-8), at most `concurrency` in
 * flight at once.
 *
 * A bulk edit across a filtered selection has to keep going when one item
 * refuses — a batch of thirty HSN updates where one variant changed in
 * Shopify a minute ago must still apply to the other twenty-nine, and the
 * person running it needs to know which one failed and why, not just a
 * count. `WriteResult` already turns an expected rejection into a value
 * rather than a throw; the `try`/`catch` here exists for the one thing it
 * does not cover — a genuine transport failure — so that a single thrown
 * item is captured as its own outcome instead of rejecting the whole batch
 * and losing every other write that was still in flight.
 *
 * A simple worker pool rather than chunking `items` into fixed-size groups:
 * chunking would let a slow item at the front of one group hold back a fast
 * item behind it in the same group while an earlier group's slot sits idle.
 * A pool keeps exactly `concurrency` writes in flight throughout, each
 * starting the moment a slot frees up.
 */
export async function runBulk<T>(
  items: readonly T[],
  write: (item: T) => Promise<WriteResult<unknown>>,
  options: {
    concurrency?: number;
    /** Called after each item settles, with how many of `items` are done so far. */
    onProgress?: (done: number, total: number) => void;
  } = {},
): Promise<BulkOutcome<T>[]> {
  const concurrency = Math.max(1, options.concurrency ?? BULK_CONCURRENCY);
  const outcomes: BulkOutcome<T>[] = Array.from({ length: items.length });
  let nextIndex = 0;
  let done = 0;

  async function runOne(index: number): Promise<void> {
    const item = items[index];
    if (item === undefined) return;
    try {
      const result = await write(item);
      outcomes[index] = result.ok
        ? { item, status: "success" }
        : { item, status: "rejected", rejection: result.rejection };
    } catch (error) {
      outcomes[index] = {
        item,
        status: "error",
        message: error instanceof Error ? error.message : "Request failed.",
      };
    }
    done += 1;
    options.onProgress?.(done, items.length);
  }

  async function worker(): Promise<void> {
    for (;;) {
      const index = nextIndex;
      nextIndex += 1;
      if (index >= items.length) return;
      await runOne(index);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length) }, () => worker()),
  );

  return outcomes;
}
